import { Alert, AlertIcon, Box, Button, Stack, Text, useDisclosure } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiMessageSquare } from "react-icons/fi";
import {
  SeV2QuestionDto,
  SeV2QuestionKind,
  SourcingEventStatus,
  SourcingEventV2Dto,
  useListBidV2SForEventQuery,
} from "../../../../autogen/bff-api";
import { formatDateTime } from "../../../../common/components/communication/communicationUtils";
import { isBidDeadlinePassed } from "../supplierRows";
import { BuyerClarificationThread } from "./BuyerClarificationThread";
import { ClarificationCounterparty, StartClarificationModal } from "./StartClarificationModal";

export const ClarificationsPanel = ({
  event,
  questions,
  supplierOrganizationId,
}: {
  event: SourcingEventV2Dto;
  questions: SeV2QuestionDto[];
  supplierOrganizationId?: string;
}) => {
  const { t } = useTranslation();
  const eventId = event.id;
  const startModal = useDisclosure();

  const deadlinePassed = isBidDeadlinePassed(event);
  const cancelled = event.status === SourcingEventStatus.Cancelled;
  const bidDeadline = event.milestoneDates?.bidSubmissionDeadline;

  // The bid list is withheld by the BFF until the deadline passes, so before then there is nobody to pick — which
  // matches the rule: a one-to-one dialog with a single tenderer is only lawful once the competition has closed.
  const { data: bidsData } = useListBidV2SForEventQuery({ eventId }, { skip: !deadlinePassed });
  const counterparties: ClarificationCounterparty[] = (bidsData?.bids ?? [])
    .filter((bid) => !supplierOrganizationId || bid.owner === supplierOrganizationId)
    .map((bid) => ({ organizationId: bid.owner, organizationName: bid.ownerName }))
    .filter(
      (counterparty, index, all) =>
        all.findIndex((c) => c.organizationId === counterparty.organizationId) === index,
    );

  const clarifications = questions
    .filter((question) => question.kind === SeV2QuestionKind.Clarification)
    .filter((question) => !supplierOrganizationId || question.supplierOrganizationId === supplierOrganizationId)
    .slice()
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));

  const canStart = deadlinePassed && !cancelled && counterparties.length > 0;

  return (
    <Stack spacing="4">
      <Alert status="info" rounded="md" fontSize="sm">
        <AlertIcon />
        {t(
          "A clarification dialog is private between you and one tenderer. It may only clarify an existing tender, never change it (FOA § 23-5).",
        )}
      </Alert>

      <Box>
        <Button
          colorScheme="teal"
          leftIcon={<FiMessageSquare />}
          onClick={startModal.onOpen}
          isDisabled={!canStart}
        >
          {t("Start a clarification dialog")}
        </Button>
        {!deadlinePassed && (
          <Text fontSize="sm" color="smMuted" mt="2">
            {t("Dialog is allowed after the submission deadline (FOA § 23-5)")}
            {bidDeadline ? `: ${formatDateTime(bidDeadline)}` : ""}.{" "}
            {t(
              "Until then, equal treatment forbids one-to-one contact with a single tenderer — questions must be answered to all.",
            )}
          </Text>
        )}
        {deadlinePassed && cancelled && (
          <Text fontSize="sm" color="smMuted" mt="2">
            {t("The competition is cancelled, so no new dialog can be started.")}
          </Text>
        )}
        {deadlinePassed && !cancelled && counterparties.length === 0 && (
          <Text fontSize="sm" color="smMuted" mt="2">
            {t("No tenders were delivered, so there is nobody to clarify with.")}
          </Text>
        )}
      </Box>

      {clarifications.length === 0 ? (
        <Box py="6" textAlign="center" color="smMuted">
          <Text>{t("No clarification dialogs yet")}</Text>
        </Box>
      ) : (
        clarifications.map((clarification) => (
          <BuyerClarificationThread key={clarification.id} eventId={eventId} clarification={clarification} />
        ))
      )}

      {startModal.isOpen && (
        <StartClarificationModal
          eventId={eventId}
          counterparties={counterparties}
          fixedCounterpartyId={supplierOrganizationId}
          isOpen={startModal.isOpen}
          onClose={startModal.onClose}
        />
      )}
    </Stack>
  );
};
